<template>
  <div class="dashboard-container">
    <div class="kpi-grid">
      <div class="kpi-card revenue">
        <div class="kpi-icon"><span class="material-symbols-outlined">payments</span></div>
        <div class="kpi-data">
          <h3>Faturamento Total</h3>
          <p>{{ dashboardData.kpis.faturamento | formatCurrency }}</p>
        </div>
      </div>
      
      <div class="kpi-card today">
        <div class="kpi-icon"><span class="material-symbols-outlined">today</span></div>
        <div class="kpi-data">
          <h3>Vendas Hoje</h3>
          <p>{{ dashboardData.kpis.vendasHoje | formatCurrency }}</p>
        </div>
      </div>
      
      <div class="kpi-card stock">
        <div class="kpi-icon"><span class="material-symbols-outlined">egg</span></div>
        <div class="kpi-data">
          <h3>Estoque (Ovos)</h3>
          <p>{{ dashboardData.kpis.estoqueAtual || 0 }}</p>
        </div>
      </div>
      
      <div class="kpi-card sales">
        <div class="kpi-icon"><span class="material-symbols-outlined">shopping_bag</span></div>
        <div class="kpi-data">
          <h3>Vendas Totais</h3>
          <p>{{ dashboardData.kpis.vendas }}</p>
        </div>
      </div>
      
      <div class="kpi-card ticket">
        <div class="kpi-icon"><span class="material-symbols-outlined">receipt_long</span></div>
        <div class="kpi-data">
          <h3>Ticket Médio</h3>
          <p>{{ dashboardData.kpis.ticketMedio | formatCurrency }}</p>
        </div>
      </div>
      
      <div class="kpi-card rejected">
        <div class="kpi-icon"><span class="material-symbols-outlined">credit_card_off</span></div>
        <div class="kpi-data">
          <h3>Recusados / Cancelados</h3>
          <p>{{ dashboardData.kpis.recusados || 0 }}</p>
        </div>
      </div>
    </div>
    
    <div class="main-stats-grid">
      
      <div class="chart-section card-box">
        <div class="chart-header">
          <h4>💰 Histórico de Vendas</h4>
          
          <div class="chart-controls">
            <select v-model="chartConfig.groupBy" @change="resetAndFetch">
              <option value="day">Por Dia</option>
              <option value="month">Por Mês</option>
            </select>
            
            <select v-model="chartConfig.limit" @change="resetAndFetch">
              <option value="7">7 períodos</option>
              <option value="15">15 períodos</option>
              <option value="30">30 períodos</option>
            </select>
          </div>
        </div>
        
        <div class="chart-nav">
          <button @click="navigate(chartConfig.limit)" class="nav-btn" title="Voltar período completo">
            <span class="material-icons-outlined">keyboard_double_arrow_left</span>
          </button>
          
          <button @click="navigate(1)" class="nav-btn" title="Voltar 1 dia/mês">
            <span class="material-icons-outlined">chevron_left</span>
          </button>
          
          <span class="date-range" v-if="chartConfig.periodStart">
            {{ formatDate(chartConfig.periodStart) }} - {{ formatDate(chartConfig.periodEnd) }}
          </span>
          
          <button @click="navigate(-1)" class="nav-btn" :disabled="chartConfig.offset <= 0" title="Avançar 1 dia/mês">
            <span class="material-icons-outlined">chevron_right</span>
          </button>
          
          <button @click="navigate(-chartConfig.limit)" class="nav-btn" :disabled="chartConfig.offset <= 0" title="Avançar período completo">
            <span class="material-icons-outlined">keyboard_double_arrow_right</span>
          </button>
        </div>
        
        <div class="chart-wrapper" v-if="loaded">
          <line-chart :chartData="dynamicChartData" :options="chartOptions" :height="250"></line-chart>
        </div>
        <div v-else class="loading-chart">Carregando gráfico...</div>
      </div>
      
      <div class="top-products-section card-box">
        <div class="chart-header">
          <h4>🏆 Produtos Mais Vendidos</h4>
        </div>
        <table class="simple-table">
          <thead>
          <tr>
            <th>Produto</th>
            <th class="text-right">Qtd</th>
            <th class="text-right">Total</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(prod, index) in dashboardData.topProdutos" :key="index">
            <td>
              <span class="rank-number">#{{ index + 1 }}</span> {{ prod.nome }}
            </td>
            <td class="text-right">{{ prod.quantidade }}</td>
            <td class="text-right highlight">{{ prod.receita | formatCurrency }}</td>
          </tr>
          <tr v-if="dashboardData.topProdutos.length === 0">
            <td colspan="3" class="text-center">Sem dados de vendas.</td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import LineChart from './charts/LineChart.vue'

export default {
  name: 'AdminDashboard',
  components: { LineChart },
  props: {
    dashboardData: { type: Object, default: () => ({ kpis: {}, topProdutos: [], graficoVendas: { labels: [], data: [] } }) }
  },
  data() {
    return {
      loaded: false,
      chartConfig: {
        groupBy: 'day',
        limit: 7,
        offset: 0,
        periodStart: null,
        periodEnd: null
      },
      // Dados locais do gráfico
      localChartData: { labels: [], data: [] },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          yAxes: [{ ticks: { beginAtZero: true, callback: (value) => 'R$ ' + value } }],
          xAxes: [{ gridLines: { display: false } }]
        },
        tooltips: {
          callbacks: {
            label: (item) => `R$ ${Number(item.yLabel).toFixed(2)}`
          }
        }
      }
    }
  },
  computed: {
    dynamicChartData() {
      return {
        labels: this.localChartData.labels,
        datasets: [
          {
            label: 'Vendas',
            borderColor: '#FF9800',
            backgroundColor: 'rgba(255, 152, 0, 0.1)',
            borderWidth: 2,
            pointBackgroundColor: '#FFFFFF',
            pointBorderColor: '#FF9800',
            pointRadius: 4,
            data: this.localChartData.data
          }
        ]
      }
    }
  },
  mounted() {
    this.fetchChartData();
  },
  methods: {
    async fetchChartData() {
      this.loaded = false;
      
      try {
        // Envia offset em vez de page
        const params = {
          groupBy: this.chartConfig.groupBy,
          limit: this.chartConfig.limit,
          offset: this.chartConfig.offset
        };
        
        const res = await api.get(`/api/admin/chart-vendas`, {
          params: params
        });
        
        this.localChartData.labels = res.data.labels;
        this.localChartData.data = res.data.data;
        this.chartConfig.periodStart = res.data.periodStart;
        this.chartConfig.periodEnd = res.data.periodEnd;
        
        this.loaded = true;
      } catch (err) {
        console.error("Erro ao carregar gráfico:", err);
      }
    },
    resetAndFetch() {
      this.chartConfig.offset = 0; // Reseta para "hoje"
      this.fetchChartData();
    },
    // Função unificada de navegação
    navigate(amount) {
      // amount positivo = vai para o passado (aumenta o offset)
      // amount negativo = vai para o futuro (diminui o offset)
      const newOffset = this.chartConfig.offset + amount;
      
      // Impede de ir para o futuro (offset negativo)
      if (newOffset < 0) {
        this.chartConfig.offset = 0;
      } else {
        this.chartConfig.offset = newOffset;
      }
      
      this.fetchChartData();
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      // Ajuste de timezone simples para exibição correta da string que vem do backend
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    }
  },
  watch: {
    dashboardData: {
      immediate: true,
      handler(val) {
        // Carrega dados iniciais se vierem via prop (opcional, já que o mounted chama o fetch)
        if (val && val.graficoVendas && val.graficoVendas.labels.length > 0 && this.localChartData.labels.length === 0) {
          // Lógica opcional para usar dados iniciais
        }
      }
    }
  },
  filters: {
    formatCurrency(value) {
      return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }
  }
}
</script>

<style scoped>
.dashboard-container {
  display: flex;
  flex-direction: column;
  gap: 25px;
  padding-bottom: 30px;
  width: 100%;
  max-width: 100%;
}

/* KPIs */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  padding: 10px;
}

.kpi-card {
  background-color: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: 10px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 20px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.05);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: pointer;
}
.kpi-card:hover {
  transform: translateY(-5px);
  background-color: var(--bg-icon-hover-light, rgba(255,255,255,0.05));
  border-color: var(--color-accent);
  box-shadow: 0 10px 20px rgba(0,0,0,0.15);
}
.kpi-icon {
  width: 50px; height: 50px;
  min-width: 50px;
  min-height: 50px;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 24px;
}

.kpi-card.revenue .kpi-icon { background: rgba(76, 175, 80, 0.15); color: #4CAF50; }
.kpi-card.sales .kpi-icon { background: rgba(33, 150, 243, 0.15); color: #2196F3; }
.kpi-card.today .kpi-icon { background: rgba(0, 188, 212, 0.15); color: #00BCD4; }
.kpi-card.stock .kpi-icon { background: rgba(255, 152, 0, 0.15); color: #FF9800; }
.kpi-card.ticket .kpi-icon { background: rgba(156, 39, 176, 0.15); color: #9C27B0; }
.kpi-card.pending .kpi-icon { background: rgba(255, 152, 0, 0.15); color: #FF9800; }
.kpi-card.rejected .kpi-icon { background: rgba(244, 67, 54, 0.15); color: #F44336; }

.kpi-data h3 { margin: 0 0 5px 0; font-size: 0.9rem; color: var(--text-secondary); font-weight: 600; text-transform: uppercase; }
.kpi-data p { margin: 0; font-size: 1.5rem; font-weight: 800; color: var(--text-main); }

/* Main Stats */
.main-stats-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  width: 100%;
  padding: 0 10px;
  box-sizing: border-box;
}

.card-box {
  background-color: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.05);
  position: relative;
  min-width: 0;
  word-wrap: break-word;
}

.card-box h4 {
  margin: 0 0 0 0;
  font-size: 1.1rem;
  color: var(--text-hero);
  padding-bottom: 0
}

.chart-wrapper {
  position: relative;
  height: 250px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

/* Tabela Simples dentro do Dashboard */
.top-products-section {
  overflow-x: auto;
}

/* Table Styles Simples */
.simple-table {
  width: 100%;
  border-collapse: collapse;
  //min-width: 200px;
}
.simple-table th { text-align: left; color: var(--text-secondary); font-size: 0.85rem; padding-bottom: 2px; padding-right: 8px; border-bottom: 1px solid var(--border-light); }
.simple-table td { border-bottom: 1px solid var(--border-light);                       padding-bottom: 5px; padding-right: 8px; color: var(--text-main); font-size: 0.95rem; }
.simple-table tr:last-child td { border-bottom: none;}

.text-right { text-align: right !important;}
.text-center { text-align: center; color: var(--text-secondary); padding: 20px; }
.rank-number { background: var(--bg-tag); color: var(--text-main); padding: 2px 6px; border-radius: 4px; font-size: 0.8rem; margin-right: 5px; font-weight: bold; }
.highlight { color: var(--color-accent); font-weight: bold; }

/* Pulse Animation para Pendentes */
.alert-pulse { border: 1px solid #FF9800; }


.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 10px;
  padding-bottom: 0;
}

.chart-header h4 {
  margin: 0;
  border: none;
  padding: 0;
}

.chart-controls {
  display: flex;
  gap: 10px;
}

.chart-controls select {
  padding: 5px;
  border-radius: 4px;
  border: 1px solid var(--border-light);
  background-color: var(--bg-main);
  color: var(--text-main);
  font-size: 0.85rem;
}

.chart-nav {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin-bottom: 10px;
  background-color: var(--bg-main);
  padding: 8px;
  border-radius: 6px;
}

.date-range {
  font-weight: bold;
  font-size: 0.9rem;
  color: var(--text-secondary);
}

.nav-btn {
  background: none;
  border: none;
  color: var(--color-accent);
  cursor: pointer;
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 0.85rem;
}
.nav-btn:disabled {
  color: var(--text-secondary);
  opacity: 0.5;
  cursor: not-allowed;
}

.loading-chart {
  height: 250px; /* A SOLUÇÃO: Mesma altura fixa do gráfico */
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  font-weight: 500;
  background-color: rgba(0, 0, 0, 0.02); /* Opcional: fundo sutil */
  border-radius: 4px;
}

/* Responsividade */
@media (min-width: 1200px) {
  .kpi-grid {
    grid-template-columns: repeat(3, 1fr); /* 3 colunas x 2 linhas */
  }
}

@media (max-width: 1100px) {
  .main-stats-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .kpi-grid {
    grid-template-columns: 1fr 1fr;
  }
  
  .kpi-card {
    padding: 15px;
    gap: 10px;
    flex-direction: column;
    text-align: center;
  }
  
  .kpi-data h3 { font-size: 0.75rem; }
  .kpi-data p { font-size: 1.1rem; }
  .kpi-icon { width: 40px; height: 40px; font-size: 20px; }
}
</style>